import {removeDuplicatesFromArray} from "helpers/filterHandlers";
import {createSelector} from "reselect";
import {allKeywordLabelSelector} from "store/filter/keywordFilterSelectors";
import {TRootState} from "types";
import {SICandidate} from "api/candidateApi/types";

export const candidatesInProgressSelector = (state: TRootState) =>
	state.candidate.candidatesInProgress;
export const filteredCandidatesInProgressSelector = (state: TRootState) =>
	state.candidate.filteredCandidatesInProgress;
export const currentCandidateSelector = (state: TRootState) => state.candidate.currentCandidate;

export const similarCandidatesInProgressSelector = (state: TRootState) =>
	state.candidate.similarCandidatesInProgress;

export const candidatesSelector = (state: TRootState) => state.candidate.candidates || [];

export const candidatesPaginationInfoSelector = (state: TRootState) =>
	state.candidate.paginationInfo;

export const similarCandidatesSelector = (state: TRootState) => state.candidate.similarCandidates;

export const similarCandidatesPaginationInfoSelector = (state: TRootState) =>
	state.candidate.similarCandidatesPaginationInfo;

export const importLinkedInCandidateInProgressSelector = (state: TRootState) =>
	state.candidate.importLinkedInCandidateInProgress;

export const importLinkedInProfileInfoSelector = (state: TRootState) =>
	state.candidate.importLinkedInProfileInfo;

export const searchCandidateForProjectSelector = (state: TRootState) =>
	state.candidate.searchCandidateForProject;

export const candidateImportByLinkedInForProjectSelector = (state: TRootState) =>
	state.candidate.candidateImportByLinkedInForProject;

export const isModalBeingDisplayedForProject = (state: TRootState) =>
	state.candidate.activeAddPeopleModalForProject;

export const anonymisedCandidatesSelector = (state: TRootState) =>
	state.candidate.anonymisedCandidates;

export const projectIdSelector = (_: TRootState, projectId: number) => projectId;

export const isLinkedInProfileImportIsInProgress = createSelector(
	[importLinkedInProfileInfoSelector],
	(importLinkedInProfileInfo) =>
		importLinkedInProfileInfo !== null && importLinkedInProfileInfo?.status !== undefined,
);

export const anonymisedCandidatePhotoCollectionSelector = (state: TRootState) =>
	state.candidate.anonymisedCandidatePhotoCollection;

export const anonymiseProfileModeTurnedOnSelector = (state: TRootState) =>
	state.candidate.anonymiseProfileModeTurnedOn;

export const getCandidateSearchInProgressByProjectId = createSelector(
	[searchCandidateForProjectSelector, projectIdSelector],
	(searchCandidateForProject, projectId) => {
		return searchCandidateForProject &&
			projectId &&
			searchCandidateForProject[projectId] !== undefined
			? searchCandidateForProject[projectId]
			: false;
	},
);

export const getCandidateImportInfoByProjectId = createSelector(
	[candidateImportByLinkedInForProjectSelector, projectIdSelector],
	(candidateImportByLinkedInForProject, projectId) => {
		return candidateImportByLinkedInForProject &&
			projectId &&
			candidateImportByLinkedInForProject[projectId] !== undefined
			? candidateImportByLinkedInForProject[projectId]
			: null;
	},
);

export const isSelectedProjectAddPeopleModalActive = createSelector(
	[isModalBeingDisplayedForProject, projectIdSelector],
	(activeModal, projectId) => {
		return activeModal === projectId;
	},
);

export const candidateSelectedKeywordLabelsSelector = createSelector(
	[allKeywordLabelSelector],
	(selectedKeyword) => {
		return removeDuplicatesFromArray([...selectedKeyword]);
	},
);

export const anonymisedWhiteMaleCandidatesSelector = createSelector(
	[anonymisedCandidatesSelector, anonymisedCandidatePhotoCollectionSelector],
	(anonymisedCandidates, anonymisedCandidatePhotoCollection) => {
		return (
			anonymisedCandidates
				?.filter((candidate: SICandidate) => {
					return candidate.ethnicity === "White" && candidate.gender === "Male";
				})
				.map((candidate: SICandidate, idx: number) => {
					const photoCollection = anonymisedCandidatePhotoCollection.white.male;

					return {
						...candidate,
						photo: photoCollection[idx % photoCollection.length],
					};
				}) || []
		);
	},
);

export const anonymisedWhiteFemaleCandidatesSelector = createSelector(
	[anonymisedCandidatesSelector, anonymisedCandidatePhotoCollectionSelector],
	(anonymisedCandidates, anonymisedCandidatePhotoCollection) => {
		return (
			anonymisedCandidates
				?.filter((candidate: SICandidate) => {
					return candidate.ethnicity === "White" && candidate.gender === "Female";
				})
				.map((candidate: SICandidate, idx: number) => {
					const photoCollection = anonymisedCandidatePhotoCollection.white.female;

					return {
						...candidate,
						photo: photoCollection[idx % photoCollection.length],
					};
				}) || []
		);
	},
);

export const anonymisedHispanicMaleCandidatesSelector = createSelector(
	[anonymisedCandidatesSelector, anonymisedCandidatePhotoCollectionSelector],
	(anonymisedCandidates, anonymisedCandidatePhotoCollection) => {
		return (
			anonymisedCandidates
				?.filter((candidate: SICandidate) => {
					return candidate.ethnicity === "Hispanic" && candidate.gender === "Male";
				})
				.map((candidate: SICandidate, idx: number) => {
					const photoCollection = anonymisedCandidatePhotoCollection.hispanic.male;

					return {
						...candidate,
						photo: photoCollection[idx % photoCollection.length],
					};
				}) || []
		);
	},
);

export const anonymisedHispanicFemaleCandidatesSelector = createSelector(
	[anonymisedCandidatesSelector, anonymisedCandidatePhotoCollectionSelector],
	(anonymisedCandidates, anonymisedCandidatePhotoCollection) => {
		return (
			anonymisedCandidates
				?.filter((candidate: SICandidate) => {
					return candidate.ethnicity === "Hispanic" && candidate.gender === "Female";
				})
				.map((candidate: SICandidate, idx: number) => {
					const photoCollection = anonymisedCandidatePhotoCollection.hispanic.female;

					return {
						...candidate,
						photo: photoCollection[idx % photoCollection.length],
					};
				}) || []
		);
	},
);

export const anonymisedSouthAsianMaleCandidatesSelector = createSelector(
	[anonymisedCandidatesSelector, anonymisedCandidatePhotoCollectionSelector],
	(anonymisedCandidates, anonymisedCandidatePhotoCollection) => {
		return (
			anonymisedCandidates
				?.filter((candidate: SICandidate) => {
					return candidate.ethnicity === "South Asian" && candidate.gender === "Male";
				})
				.map((candidate: SICandidate, idx: number) => {
					const photoCollection = anonymisedCandidatePhotoCollection.southAsian.male;

					return {
						...candidate,
						photo: photoCollection[idx % photoCollection.length],
					};
				}) || []
		);
	},
);

export const anonymisedSouthAsianFemaleCandidatesSelector = createSelector(
	[anonymisedCandidatesSelector, anonymisedCandidatePhotoCollectionSelector],
	(anonymisedCandidates, anonymisedCandidatePhotoCollection) => {
		return (
			anonymisedCandidates
				?.filter((candidate: SICandidate) => {
					return candidate.ethnicity === "South Asian" && candidate.gender === "Female";
				})
				.map((candidate: SICandidate, idx: number) => {
					const photoCollection = anonymisedCandidatePhotoCollection.southAsian.female;

					return {
						...candidate,
						photo: photoCollection[idx % photoCollection.length],
					};
				}) || []
		);
	},
);

export const anonymisedEastAsianMaleCandidatesSelector = createSelector(
	[anonymisedCandidatesSelector, anonymisedCandidatePhotoCollectionSelector],
	(anonymisedCandidates, anonymisedCandidatePhotoCollection) => {
		return (
			anonymisedCandidates
				?.filter((candidate: SICandidate) => {
					return candidate.ethnicity === "East Asian" && candidate.gender === "Male";
				})
				.map((candidate: SICandidate, idx: number) => {
					const photoCollection = anonymisedCandidatePhotoCollection.eastAsian.male;

					return {
						...candidate,
						photo: photoCollection[idx % photoCollection.length],
					};
				}) || []
		);
	},
);

export const anonymisedEastAsianFemaleCandidatesSelector = createSelector(
	[anonymisedCandidatesSelector, anonymisedCandidatePhotoCollectionSelector],
	(anonymisedCandidates, anonymisedCandidatePhotoCollection) => {
		return (
			anonymisedCandidates
				?.filter((candidate: SICandidate) => {
					return candidate.ethnicity === "East Asian" && candidate.gender === "Female";
				})
				.map((candidate: SICandidate, idx: number) => {
					const photoCollection = anonymisedCandidatePhotoCollection.eastAsian.female;

					return {
						...candidate,
						photo: photoCollection[idx % photoCollection.length],
					};
				}) || []
		);
	},
);

export const anonymisedMiddleEasternMaleCandidatesSelector = createSelector(
	[anonymisedCandidatesSelector, anonymisedCandidatePhotoCollectionSelector],
	(anonymisedCandidates, anonymisedCandidatePhotoCollection) => {
		return (
			anonymisedCandidates
				?.filter((candidate: SICandidate) => {
					return candidate.ethnicity === "Middle Eastern" && candidate.gender === "Male";
				})
				.map((candidate: SICandidate, idx: number) => {
					const photoCollection = anonymisedCandidatePhotoCollection.middleEastern.male;

					return {
						...candidate,
						photo: photoCollection[idx % photoCollection.length],
					};
				}) || []
		);
	},
);

export const anonymisedMiddleEasternFemaleCandidatesSelector = createSelector(
	[anonymisedCandidatesSelector, anonymisedCandidatePhotoCollectionSelector],
	(anonymisedCandidates, anonymisedCandidatePhotoCollection) => {
		return (
			anonymisedCandidates
				?.filter((candidate: SICandidate) => {
					return (
						candidate.ethnicity === "Middle Eastern" && candidate.gender === "Female"
					);
				})
				.map((candidate: SICandidate, idx: number) => {
					const photoCollection = anonymisedCandidatePhotoCollection.middleEastern.female;

					return {
						...candidate,
						photo: photoCollection[idx % photoCollection.length],
					};
				}) || []
		);
	},
);

export const anonymisedBlackMaleCandidatesSelector = createSelector(
	[anonymisedCandidatesSelector, anonymisedCandidatePhotoCollectionSelector],
	(anonymisedCandidates, anonymisedCandidatePhotoCollection) => {
		return (
			anonymisedCandidates
				?.filter((candidate: SICandidate) => {
					return candidate.ethnicity === "Black" && candidate.gender === "Male";
				})
				.map((candidate: SICandidate, idx: number) => {
					const photoCollection = anonymisedCandidatePhotoCollection.black.male;

					return {
						...candidate,
						photo: photoCollection[idx % photoCollection.length],
					};
				}) || []
		);
	},
);

export const anonymisedBlackFemaleCandidatesSelector = createSelector(
	[anonymisedCandidatesSelector, anonymisedCandidatePhotoCollectionSelector],
	(anonymisedCandidates, anonymisedCandidatePhotoCollection) => {
		return (
			anonymisedCandidates
				?.filter((candidate: SICandidate) => {
					return candidate.ethnicity === "Black" && candidate.gender === "Female";
				})
				.map((candidate: SICandidate, idx: number) => {
					const photoCollection = anonymisedCandidatePhotoCollection.black.female;

					return {
						...candidate,
						photo: photoCollection[idx % photoCollection.length],
					};
				}) || []
		);
	},
);
